function fmt(n) {
  if (n === null || n === undefined) return '—';
  const abs = Math.abs(n);
  const str = abs >= 1000 ? `$${(abs/1000).toFixed(1)}k` : `$${abs.toFixed(0)}`;
  return n < 0 ? `-${str}` : str;
}

// ── CELL STYLES ──────────────────────────────────────────
const actualCellStyle = {
  padding: '0.4rem 0.5rem',
  textAlign: 'right',
  background: '#F1F5F9',
  color: '#475569',
  fontSize: '0.82rem',
  borderRight: '1px solid #E2E8F0',
  whiteSpace: 'nowrap',
};

const forecastCellStyle = {
  padding: '0.4rem 0.5rem',
  textAlign: 'right',
  background: '#EFF6FF',
  color: '#1E40AF',
  fontSize: '0.82rem',
  borderRight: '1px solid #DBEAFE',
  whiteSpace: 'nowrap',
};

const totalCellStyle = {
  padding: '0.4rem 0.6rem',
  textAlign: 'right',
  background: '#F8FAFC',
  fontWeight: 600,
  fontSize: '0.82rem',
  borderLeft: '2px solid #E2E8F0',
  whiteSpace: 'nowrap',
};

// ── SECTION HEADER ───────────────────────────────────────
function SectionHeader({ label, colCount, bg = '#1B2A4A' }) {
  // Light bg (e.g. #FEF3C7) → dark amber text; dark bg → light text
  const color = bg === '#FEF3C7' ? '#92400E'
              : bg === '#1B2A4A' ? '#94A3B8'
              : '#fff';
  return (
    <tr>
      <td colSpan={colCount} style={{
        background: bg, color,
        padding: '0.4rem 0.75rem',
        fontSize: '0.72rem', fontWeight: 700,
        letterSpacing: '0.06em', textTransform: 'uppercase',
      }}>
        {label}
      </td>
    </tr>
  );
}

// ── SUBTOTAL ROW ─────────────────────────────────────────
// isIncomeType: true = income (positive variance = good)
//               false = expense/cogs (positive variance = bad)
function SubtotalRow({ label, monthValues, total, budget, closedCount,
                       bg='#E8EDF5', color='#1B2A4A', bold=false, isIncomeType=true }) {
  const variance = isIncomeType
    ? total - budget
    : budget - total;
  const vColor = variance >= 0 ? '#15803D' : '#BE123C';

  return (
    <tr style={{ background: bg, borderTop: '2px solid #CBD5E1' }}>
      <td style={{ padding:'0.5rem 0.75rem', fontWeight: bold?700:600,
                   color, fontSize: bold?'0.88rem':'0.83rem',
                   position:'sticky', left:0, background:bg,
                   borderRight:'2px solid #CBD5E1' }}>
        {label}
      </td>
      {monthValues.map((v, i) => (
        <td key={i} style={{
          padding: '0.4rem 0.5rem', textAlign: 'right',
          fontWeight: bold?700:600, fontSize: '0.82rem',
          color: v < 0 ? '#BE123C' : color,
          background: i < closedCount ? '#E2E8F0' : '#DBEAFE',
          borderRight: '1px solid #CBD5E1',
          whiteSpace: 'nowrap',
        }}>
          {fmt(v)}
        </td>
      ))}
      <td style={{ ...totalCellStyle, fontWeight:bold?700:600, color,
                   position:'sticky', right:160, zIndex:1 }}>
        {fmt(total)}
      </td>
      <td style={{ ...totalCellStyle, color:'#64748B', fontWeight:400,
                   position:'sticky', right:80, zIndex:1 }}>
        {fmt(budget)}
      </td>
      <td style={{ ...totalCellStyle, color:vColor,
                   position:'sticky', right:0, zIndex:1 }}>
        {variance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(variance))}
      </td>
    </tr>
  );
}

// ── MAIN FORECAST GRID ───────────────────────────────────
function ForecastGrid({ data }) {
  if (!data) return null;

  const { rows, monthNames, closedMonths, hasPromotedScenario } = data;
  const monthCount    = monthNames.length;
  const visibleMonths = monthNames.map((m, i) => ({ name: m.substring(0,3), index: i }));

  // P&L grouping
  const incomeRows = (rows || []).filter(r =>
    r.section === 'Income' &&
    r.section !== 'Other Income' &&
    r.section !== 'OtherIncome'
  );

  const cogsRows = rows.filter(r =>
    r.section === 'COGS' || r.section === 'Cost of Goods Sold'
  );

  const unbudgetedRows = (rows || []).filter(r => r.isUnbudgeted === true);

  const otherIncomeRows2  = (rows || []).filter(r =>
    r.section === 'Other Income' || r.section === 'OtherIncome'
  );
  const otherExpenseRows2 = (rows || []).filter(r =>
    r.section === 'Other Expense'  || r.section === 'OtherExpense' ||
    r.section === 'Other Expenses' || r.section === 'OtherExpenses'
  );

  const expenseRows = (rows || []).filter(r =>
    !r.isUnbudgeted &&
    r.section !== 'Other Expense' &&
    r.section !== 'OtherExpense' &&
    r.section !== 'Other Expenses' &&
    r.section !== 'OtherExpenses' &&
    r.section !== 'Other Income' &&
    r.section !== 'OtherIncome' &&
    (r.section === 'Expense' || r.section === 'Expenses')
  );

  // Monthly subtotals
  const sumMo  = (rows, i) => rows.reduce((s,r) => s + (r.months[i]?.value || 0), 0);
  const incMo  = Array.from({length:monthCount}, (_,i) => sumMo(incomeRows,  i));
  const cogsMo = Array.from({length:monthCount}, (_,i) => sumMo(cogsRows,    i));
  const expMo  = Array.from({length:monthCount}, (_,i) => sumMo(expenseRows, i));
  const grossMo      = incMo.map((v,i) => v - cogsMo[i]);
  const netMo        = incMo.map((v,i) => v - cogsMo[i] - expMo[i]);
  const otherIncMo   = Array.from({length:monthCount}, (_,i) => sumMo(otherIncomeRows2,  i));
  const otherExpMo   = Array.from({length:monthCount}, (_,i) => sumMo(otherExpenseRows2, i));
  const unbudgetedMo = Array.from({length:monthCount}, (_,i) => sumMo(unbudgetedRows,    i));
  const netIncomeMo  = incMo.map((v,i) =>
    v - cogsMo[i] - expMo[i] +
    otherIncMo[i] - otherExpMo[i] - unbudgetedMo[i]
  );

  const sumTotal  = r => r.reduce((s,x) => s + x.total,       0);
  const sumBudget = r => r.reduce((s,x) => s + x.budgetTotal, 0);

  const colCount = monthCount + 4;

  // Render a data row
  // Since server now returns:
  //   income variance  = actual - budget (positive = over budget = favorable)
  //   expense variance = budget - actual (positive = under budget = favorable)
  // Both types: positive variance = green, negative = red
  function renderRow(row) {
    const varColor = row.variance >= 0 ? '#15803D' : '#BE123C';
    const rowBg    = row.isUnbudgeted ? '#FFFBEB' : '#fff';

    return (
      <tr key={row.category} style={{ borderBottom:'1px solid #F1F5F9', background: rowBg }}>
        <td style={{
            padding:'0.45rem 0.75rem 0.45rem 1.25rem',
            fontSize:'0.82rem', color: row.isUnbudgeted ? '#92400E' : '#475569',
            position:'sticky', left:0, zIndex:1,
            background: rowBg, borderRight:'2px solid #E2E8F0',
            whiteSpace:'nowrap',
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            <span title={row.category}>
              {row.displayName || row.leafName || row.category}
            </span>
          </td>
        {row.months.map((cell, mi) => (
          <td key={mi} style={cell.isActual ? actualCellStyle : forecastCellStyle}>
            {fmt(cell.value)}
          </td>
        ))}
        <td style={totalCellStyle}>{fmt(row.total)}</td>
        <td style={{ ...totalCellStyle, color:'#64748B', fontWeight:400 }}>
          {fmt(row.budgetTotal)}
        </td>
        <td style={{ ...totalCellStyle, color:varColor }}>
          {row.variance >= 0 ? '▲ ' : '▼ '}{fmt(Math.abs(row.variance))}
        </td>
      </tr>
    );
  }

  // Net income variance — income type (positive = good)
  const netTotal        = sumTotal(incomeRows)  - sumTotal(cogsRows)  - sumTotal(expenseRows);
  const netBudget       = sumBudget(incomeRows) - sumBudget(cogsRows) - sumBudget(expenseRows);
  const otherIncTotal   = sumTotal(otherIncomeRows2);
  const otherExpTotal   = sumTotal(otherExpenseRows2);
  const unbudgetedTotal = unbudgetedRows.reduce((s, r) => s + r.total, 0);
  const netIncomeTotal  = netTotal + otherIncTotal - otherExpTotal - unbudgetedTotal;
  const otherIncBudget  = sumBudget(otherIncomeRows2);
  const otherExpBudget  = sumBudget(otherExpenseRows2);
  const netIncomeBudget = netBudget + otherIncBudget - otherExpBudget;

  return (
    <div>
      {/* Legend */}
      <div style={{ display:'flex', gap:'1.5rem', marginBottom:'0.75rem',
                    fontSize:'0.8rem', color:'#64748B', flexWrap:'wrap' }}>
        <span>
          <span style={{ display:'inline-block', width:14, height:14,
                         background:'#F1F5F9', border:'1px solid #CBD5E1',
                         borderRadius:2, marginRight:4, verticalAlign:'middle' }} />
          QB Actuals (locked)
        </span>
        <span>
          <span style={{ display:'inline-block', width:14, height:14,
                         background:'#EFF6FF', border:'1px solid #BFDBFE',
                         borderRadius:2, marginRight:4, verticalAlign:'middle' }} />
          {hasPromotedScenario ? 'Promoted Scenario' : 'Budget Forecast'} (locked)
        </span>
        {hasPromotedScenario && (
          <span style={{ color:'#15803D', fontWeight:600 }}>
            ✓ Showing promoted scenario values for forecast months
          </span>
        )}
      </div>

      {/* Grid */}
      <div style={{ overflowX:'auto', width:'100%', background:'#fff', borderRadius:8,
                    boxShadow:'0 1px 3px rgba(0,0,0,0.08)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse',
                        fontSize:'0.82rem', minWidth:1100 }}>

          {/* Column headers */}
          <thead>
            <tr>
              <th style={{ background:'#1B2A4A', color:'#fff',
                           padding:'0.65rem 0.75rem', textAlign:'left',
                           position:'sticky', left:0, zIndex:2, minWidth:180,
                           fontSize:'0.82rem' }}>
                Category
              </th>
              {visibleMonths.map(({name, index}) => (
                <th key={index} style={{
                  background: index < closedMonths ? '#334155' : '#1E3A5F',
                  color:      index < closedMonths ? '#CBD5E1'  : '#BFDBFE',
                  padding:    '0.5rem 0.4rem',
                  textAlign:  'right',
                  minWidth:   68, fontSize:'0.75rem',
                }}>
                  {name}
                  <div style={{ fontSize:'0.62rem', marginTop:'0.1rem',
                                 color: index < closedMonths ? '#64748B' : '#93C5FD' }}>
                    {index < closedMonths ? 'ACTUAL' : 'FCST'}
                  </div>
                </th>
              ))}
              <th style={{ background:'#1B2A4A', color:'#fff',
                          padding:'0.5rem 0.6rem', textAlign:'right',
                          minWidth:80, fontSize:'0.75rem',
                          position:'sticky', right:160, zIndex:2 }}>Total</th>
              <th style={{ background:'#1B2A4A', color:'#94A3B8',
                          padding:'0.5rem 0.6rem', textAlign:'right',
                          minWidth:80, fontSize:'0.75rem',
                          position:'sticky', right:80, zIndex:2 }}>Budget</th>
              <th style={{ background:'#1B2A4A', color:'#94A3B8',
                          padding:'0.5rem 0.6rem', textAlign:'right',
                          minWidth:80, fontSize:'0.75rem',
                          position:'sticky', right:0, zIndex:2 }}>Variance</th>
            </tr>
          </thead>

          <tbody>
            {/* INCOME */}
            <SectionHeader label="📈 Income" colCount={colCount} />
            {incomeRows.map(renderRow)}
            <SubtotalRow
              label="Total Income"
              monthValues={incMo}
              total={sumTotal(incomeRows)}
              budget={sumBudget(incomeRows)}
              closedCount={closedMonths}
              bg="#EFF6FF" color="#0369A1"
              isIncomeType={true}
            />

            {/* COGS */}
            {cogsRows.length > 0 && (
              <>
                <SectionHeader label="🔧 Cost of Goods Sold" colCount={colCount} />
                {cogsRows.map(renderRow)}
                <SubtotalRow
                  label="Total COGS"
                  monthValues={cogsMo}
                  total={sumTotal(cogsRows)}
                  budget={sumBudget(cogsRows)}
                  closedCount={closedMonths}
                  bg="#FFF7ED" color="#92400E"
                  isIncomeType={false}
                />
                <SubtotalRow
                  label="Gross Profit"
                  monthValues={grossMo}
                  total={sumTotal(incomeRows) - sumTotal(cogsRows)}
                  budget={sumBudget(incomeRows) - sumBudget(cogsRows)}
                  closedCount={closedMonths}
                  bg="#F0FDF4" color="#15803D"
                  bold={true}
                  isIncomeType={true}
                />
              </>
            )}

            {/* EXPENSES */}
            <SectionHeader label="📉 Expenses" colCount={colCount} />
            {expenseRows.map(renderRow)}
            <SubtotalRow
              label="Total Expenses"
              monthValues={expMo}
              total={sumTotal(expenseRows)}
              budget={sumBudget(expenseRows)}
              closedCount={closedMonths}
              bg="#FFF1F2" color="#BE123C"
              isIncomeType={false}
            />

            {/* OTHER INCOME */}
            {otherIncomeRows2.length > 0 && (
              <>
                <tr style={{ background:'#0369A1' }}>
                  <td colSpan={monthCount + 4}
                    style={{ padding:'0.45rem 0.75rem', color:'#DBEAFE',
                             fontSize:'0.75rem', fontWeight:700,
                             letterSpacing:'0.06em', textTransform:'uppercase' }}>
                    📊 Other Income
                  </td>
                </tr>
                {otherIncomeRows2.map(renderRow)}
              </>
            )}

            {/* OTHER EXPENSES */}
            {otherExpenseRows2.length > 0 && (
              <>
                <tr style={{ background:'#7E22CE' }}>
                  <td colSpan={monthCount + 4}
                    style={{ padding:'0.45rem 0.75rem', color:'#EDE9FE',
                             fontSize:'0.75rem', fontWeight:700,
                             letterSpacing:'0.06em', textTransform:'uppercase' }}>
                    📊 Other Expenses
                  </td>
                </tr>
                {otherExpenseRows2.map(renderRow)}
              </>
            )}

            {/* UNBUDGETED ACTUALS */}
            {unbudgetedRows.length > 0 && (
              <>
                <tr style={{ background:'#B45309' }}>
                  <td colSpan={monthCount + 4}
                    style={{ padding:'0.45rem 0.75rem', color:'#FEF3C7',
                             fontSize:'0.75rem', fontWeight:700,
                             letterSpacing:'0.06em', textTransform:'uppercase' }}>
                    ⚠ Unbudgeted Actuals — transactions with no budget row in QB
                  </td>
                </tr>
                {unbudgetedRows.map(row => (
                  <tr key={`unbudgeted-${row.category}`}
                    style={{ borderBottom:'1px solid #FEF3C7', background:'#FFFBEB' }}>
                    <td style={{
                      padding:'0.5rem 0.5rem 0.5rem 1.25rem',
                      fontSize:'0.85rem', color:'#B45309',
                      whiteSpace:'nowrap', maxWidth:180,
                      overflow:'hidden', textOverflow:'ellipsis',
                    }}>
                      <span title={row.category}>⚠ {row.displayName || row.category}</span>
                    </td>
                    {row.months.map((cell, mi) => (
                      <td key={mi} style={{
                        textAlign:'right', padding:'0 0.5rem',
                        background: cell.isActual ? '#FEF3C7' : '#FFFBEB',
                        color: cell.value !== 0 ? '#B45309' : '#CBD5E1',
                        fontSize:'0.85rem',
                      }}>
                        {cell.value !== 0 ? `$${Math.abs(cell.value).toFixed(0)}` : '—'}
                      </td>
                    ))}
                    <td style={{ textAlign:'right', fontWeight:600,
                                 padding:'0 0.75rem', color:'#B45309',
                                 position:'sticky', right:160, zIndex:1,
                                 background:'#FFFBEB' }}>
                      ${row.total.toFixed(0)}
                    </td>
                    <td style={{ textAlign:'right', padding:'0 0.75rem',
                                 color:'#94A3B8', position:'sticky',
                                 right:80, zIndex:1, background:'#FFFBEB' }}>
                      —
                    </td>
                    <td style={{ textAlign:'right', fontWeight:600,
                                 padding:'0 0.75rem', color:'#B45309',
                                 position:'sticky', right:0, zIndex:1,
                                 background:'#FFFBEB' }}>
                      ▼ ${row.total.toFixed(0)} unbudgeted
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>

          <tfoot>
            {/* NET OPERATING INCOME */}
            <tr style={{ background:'#1B2A4A', color:'#fff', fontWeight:700 }}>
              <td style={{ padding:'0.7rem 0.75rem', position:'sticky', left:0,
                           background:'#1B2A4A', fontSize:'0.85rem' }}>
                NET OPERATING INCOME
              </td>
              {netMo.map((v, i) => (
                <td key={i} style={{ textAlign:'right', padding:'0 0.5rem',
                                     color: v >= 0 ? '#86EFAC' : '#FCA5A5',
                                     fontSize:'0.85rem' }}>
                  {fmt(v)}
                </td>
              ))}
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:700,
                           position:'sticky', right:160, zIndex:1, background:'#1B2A4A',
                           color: netTotal >= 0 ? '#86EFAC' : '#FCA5A5' }}>
                {fmt(netTotal)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', color:'#94A3B8',
                           position:'sticky', right:80, zIndex:1, background:'#1B2A4A' }}>
                {fmt(netBudget)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:700,
                           position:'sticky', right:0, zIndex:1, background:'#1B2A4A',
                           color: (netTotal - netBudget) >= 0 ? '#86EFAC' : '#FCA5A5' }}>
                {(netTotal - netBudget) >= 0 ? '▲ ' : '▼ '}
                {fmt(Math.abs(netTotal - netBudget))}
              </td>
            </tr>
            {/* NET INCOME — includes Other Income and Other Expenses */}
            <tr style={{ background:'#0F172A', color:'#fff', fontWeight:800,
                         borderTop:'2px solid #5EEAD4' }}>
              <td style={{ padding:'0.8rem 0.75rem', position:'sticky', left:0,
                           background:'#0F172A', fontSize:'0.9rem' }}>
                NET INCOME
              </td>
              {netIncomeMo.map((v, i) => (
                <td key={i} style={{ textAlign:'right', padding:'0 0.5rem',
                                     color: v >= 0 ? '#86EFAC' : '#FCA5A5',
                                     fontSize:'0.85rem' }}>
                  {fmt(v)}
                </td>
              ))}
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:800,
                           position:'sticky', right:160, zIndex:1, background:'#0F172A',
                           color: netIncomeTotal >= 0 ? '#86EFAC' : '#FCA5A5',
                           fontSize:'0.9rem' }}>
                {fmt(netIncomeTotal)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', color:'#94A3B8',
                           position:'sticky', right:80, zIndex:1, background:'#0F172A' }}>
                {fmt(netIncomeBudget)}
              </td>
              <td style={{ textAlign:'right', padding:'0 0.75rem', fontWeight:800,
                           position:'sticky', right:0, zIndex:1, background:'#0F172A',
                           color: (netIncomeTotal - netIncomeBudget) >= 0 ? '#86EFAC' : '#FCA5A5',
                           fontSize:'0.9rem' }}>
                {(netIncomeTotal - netIncomeBudget) >= 0 ? '▲ ' : '▼ '}
                {fmt(Math.abs(netIncomeTotal - netIncomeBudget))}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default ForecastGrid;